import { useMemo } from 'react'

import { useExhibitors, useShowStats } from '@/api/hooks'
import { useActorPermissions } from '@/hooks/useActorPermissions'
import { summarizeEntrantList, type EnteredClasses } from '@/lib/run-section-status'

/**
 * The classes with counted entries, from the source this viewer may read: show stats for report
 * readers such as judges, who cannot read the entrant list, otherwise the entrant list for intake
 * staff. A viewer who can read neither gets no data and makes no request.
 */
export function useEnteredClasses(
  showId: string | undefined,
  options?: { enabled?: boolean }
): {
  entered: EnteredClasses | null
  isLoading: boolean
  isError: boolean
  error: Error | null
} {
  const enabled = (options?.enabled ?? true) && Boolean(showId)
  const permissions = useActorPermissions(showId)
  const fromStats = permissions.can('reports:results')
  const fromEntrants = !fromStats && permissions.can('exhibitors:write')
  const statsQuery = useShowStats(enabled && fromStats ? showId : undefined)
  const exhibitorsQuery = useExhibitors(
    { showId: enabled ? showId : undefined, limit: 500 },
    { enabled: enabled && fromEntrants }
  )

  const statsData = statsQuery.data
  const exhibitorsData = exhibitorsQuery.data
  const entered = useMemo(() => {
    if (fromStats) {
      return statsData
        ? { classIds: statsData.enteredClassIds, entriesCount: statsData.entriesCount }
        : null
    }
    return exhibitorsData ? summarizeEntrantList(exhibitorsData) : null
  }, [fromStats, statsData, exhibitorsData])

  const source = fromStats ? statsQuery : exhibitorsQuery
  return {
    entered,
    isLoading: source.isLoading,
    // A failed refresh keeps the last classes read, as the stats card keeps its last counts.
    isError: source.isError && !source.data,
    error: source.data ? null : (source.error ?? null),
  }
}
